import React from "react";
import { Minus, Plus, Target, Lock, Zap, Sparkles, Users } from "lucide-react";
import { GROUP_TIERS, getGroupTier, nextGroupTier } from "@/data/site";
import { cn } from "@/lib/utils";

const fmt = (n, cur) => (cur === "INR" ? "₹" + Math.round(n).toLocaleString("en-IN") : "$" + Math.round(n).toLocaleString("en-US"));

/**
 * Squad-discount picker used inside the Register page.
 * Props:
 *   quantity, setQuantity — controlled state
 *   basePerDelegate — pre-GST base price for 1 delegate at the current window
 *   currency — "INR" | "USD"
 *   priceBreakdown — { gross, discount, net, gst, total }
 */
export default function GroupDiscountCard({ quantity, setQuantity, basePerDelegate, currency, priceBreakdown }) {
  const tier = getGroupTier(quantity);
  const nxt = nextGroupTier(quantity);
  const currentTierIdx = GROUP_TIERS.findIndex((t) => t.key === tier.key);

  // Savings to unlock next tier (extra discount vs current tier at qty+1 batch)
  let extraSavings = 0;
  if (nxt) {
    const nextQty = nxt.min;
    const nextGross = basePerDelegate * nextQty;
    const nextDiscount = Math.round(nextGross * nxt.discount / 100);
    const currentAtNextQty = Math.round(basePerDelegate * nextQty * tier.discount / 100);
    extraSavings = nextDiscount - currentAtNextQty;
  }

  const dec = () => setQuantity(Math.max(1, quantity - 1));
  const inc = () => setQuantity(Math.min(20, quantity + 1));

  return (
    <div data-testid="squad-discount" className="rounded-3xl border-2 border-emerald2/25 bg-gradient-to-br from-emerald2/[0.04] to-lime/[0.06] p-6 md:p-7 shadow-sm">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald2 text-lime shadow-md">
          <Users className="h-5 w-5" strokeWidth={2.5} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald2">Squad discount</span>
            <span className="text-[9px] font-semibold uppercase tracking-wider text-emerald2/70">· auto-applied</span>
          </div>
          <h3 className="mt-1 font-display text-xl md:text-2xl font-bold text-navy leading-tight">
            Bring your team — unlock up to 30% off
          </h3>
        </div>
      </div>

      {/* Quantity + tier badge */}
      <div className="mt-6 flex items-center gap-3 md:gap-5">
        <div className="flex items-center gap-3">
          <button
            data-testid="qty-decrement"
            type="button"
            onClick={dec}
            disabled={quantity <= 1}
            className={cn(
              "flex h-11 w-11 items-center justify-center rounded-full border-2 transition-all",
              quantity <= 1
                ? "border-navy/10 text-navy/25 cursor-not-allowed"
                : "border-navy/20 text-navy hover:border-emerald2 hover:text-emerald2 hover:bg-white"
            )}
          >
            <Minus className="h-4 w-4" strokeWidth={3} />
          </button>
          <div className="min-w-[70px] text-center">
            <div data-testid="qty-value" className="font-display text-3xl font-bold text-navy leading-none">{quantity}</div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-navy/50">{quantity === 1 ? "delegate" : "delegates"}</div>
          </div>
          <button
            data-testid="qty-increment"
            type="button"
            onClick={inc}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-ethanolOrange text-navy shadow-md hover:-translate-y-0.5 hover:bg-orangeDeep transition-all"
          >
            <Plus className="h-4 w-4" strokeWidth={3} />
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2 rounded-full border border-navy/10 bg-white/70 backdrop-blur px-4 py-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ethanolOrange/20">
            <Target className="h-4 w-4 text-ethanolOrange" strokeWidth={2.5} />
          </div>
          <div className="text-right sm:text-left">
            <div className="text-[9px] font-semibold uppercase tracking-widest text-navy/55">Tier unlocked</div>
            <div className="font-display text-sm font-bold text-navy leading-none">{tier.label}{tier.discount > 0 && ` · ${tier.discount}% off`}</div>
          </div>
        </div>
      </div>

      {/* Tier progress rail */}
      <div className="mt-7">
        <div className="relative flex items-start justify-between">
          {/* connecting line (behind icons) */}
          <div className="absolute left-5 right-5 top-5 h-0.5 bg-navy/10" aria-hidden />
          <div
            className="absolute left-5 top-5 h-0.5 bg-emerald2 transition-all duration-500"
            style={{ width: `calc(${(currentTierIdx / (GROUP_TIERS.length - 1)) * 100}% - ${currentTierIdx === 0 ? "0px" : (currentTierIdx === GROUP_TIERS.length - 1 ? "40px" : "20px")})` }}
            aria-hidden
          />
          {GROUP_TIERS.map((t, i) => {
            const isActive = i === currentTierIdx;
            const isDone = i < currentTierIdx;
            return (
              <div key={t.key} className="relative z-10 flex flex-1 flex-col items-center gap-1.5">
                <div
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all",
                    isActive
                      ? "border-ethanolOrange bg-ethanolOrange text-navy shadow-lg shadow-ethanolOrange/40 scale-110"
                      : isDone
                      ? "border-emerald2 bg-emerald2 text-lime"
                      : "border-navy/15 bg-white text-navy/30"
                  )}
                >
                  {isActive ? <Target className="h-4 w-4" strokeWidth={2.5} /> : isDone ? <Sparkles className="h-4 w-4" /> : <Lock className="h-3.5 w-3.5" />}
                </div>
                <div className={cn(
                  "text-center text-[10px] font-bold uppercase tracking-wider",
                  isActive ? "text-navy" : isDone ? "text-emerald2" : "text-navy/40"
                )}>
                  {t.label}
                </div>
                <div className={cn("text-[9px] text-center", isActive ? "text-navy/60" : "text-navy/35")}>
                  {t.hint}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Gross / You save / You pay */}
      <div className="mt-6 grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-navy/10 bg-white p-4">
          <div className="text-[9px] font-bold uppercase tracking-widest text-navy/50">Gross</div>
          <div data-testid="price-gross" className="mt-1 font-display text-lg md:text-xl font-bold text-navy leading-tight">{fmt(priceBreakdown.gross, currency)}</div>
        </div>
        <div className={cn(
          "rounded-2xl border p-4",
          priceBreakdown.discount > 0 ? "border-emerald2/30 bg-emerald2/5" : "border-navy/10 bg-white"
        )}>
          <div className={cn("text-[9px] font-bold uppercase tracking-widest", priceBreakdown.discount > 0 ? "text-emerald2" : "text-navy/50")}>You save</div>
          <div data-testid="price-save" className={cn("mt-1 font-display text-lg md:text-xl font-bold leading-tight", priceBreakdown.discount > 0 ? "text-emerald2" : "text-navy/60")}>
            {fmt(priceBreakdown.discount, currency)}
          </div>
        </div>
        <div className="rounded-2xl bg-navy p-4 text-white">
          <div className="text-[9px] font-bold uppercase tracking-widest text-ethanolOrange">You pay</div>
          <div data-testid="price-pay" className="mt-1 font-display text-lg md:text-xl font-bold leading-tight">{fmt(priceBreakdown.total, currency)}</div>
          {currency === "INR" && <div className="mt-0.5 text-[9px] font-medium text-white/60">incl. 18% GST</div>}
        </div>
      </div>

      {/* Level-up nudge */}
      {nxt && extraSavings > 0 && (
        <button
          data-testid="level-up"
          type="button"
          onClick={inc}
          className="mt-5 flex w-full items-center gap-3 rounded-2xl border-2 border-dashed border-ethanolOrange/60 bg-ethanolOrange/10 p-4 text-left transition-all hover:bg-ethanolOrange/15 hover:border-ethanolOrange"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
            <Sparkles className="h-4 w-4 text-ethanolOrange" fill="currentColor" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-navy">
              Add 1 more delegate → save an extra <span className="text-emeraldDeep font-bold">{fmt(extraSavings, currency)}</span>
            </div>
            <div className="mt-0.5 text-xs text-navy/60 flex items-center gap-1">
              Unlock <span className="font-bold text-navy">{nxt.label}</span> tier · {nxt.discount}% off
              <Zap className="h-3 w-3 text-ethanolOrange" fill="currentColor" />
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 rounded-full bg-ethanolOrange px-4 py-2 text-xs font-bold uppercase tracking-wider text-navy shadow-sm">
            Level up →
          </div>
        </button>
      )}
      {!nxt && (
        <div className="mt-5 flex items-center gap-3 rounded-2xl bg-emerald2 px-4 py-3 text-white">
          <Sparkles className="h-4 w-4 text-lime" fill="currentColor" />
          <div className="text-sm font-semibold">Maximum tier unlocked — you're saving {tier.discount}% on the whole group.</div>
        </div>
      )}
    </div>
  );
}
